import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from 'context/StoreContext';
import { whatsappUrl } from 'utils/whatsapp';
import { WhatsAppIcon } from './icons';

// Pages with their own sticky actions or WhatsApp links keep the corner free.
const HIDDEN_ON = [/^\/produk\//, /^\/keranjang/, /^\/checkout/, /^\/pembayaran\//, /^\/lacak\//, /^\/pesanan-saya/];

// On phones the first screen is the hero and its copy runs the full width, so
// the button would sit on top of the text. Show it once the visitor scrolls.
const MOBILE_QUERY = '(max-width: 767px)';
const MOBILE_REVEAL_AFTER_PX = 480;

function useRevealOnMobile() {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const mobile = window.matchMedia(MOBILE_QUERY);
    const update = () => setVisible(!mobile.matches || window.scrollY > MOBILE_REVEAL_AFTER_PX);
    update();
    window.addEventListener('scroll', update, { passive: true });
    mobile.addEventListener('change', update);
    return () => {
      window.removeEventListener('scroll', update);
      mobile.removeEventListener('change', update);
    };
  }, []);

  return visible;
}

/** Floating "chat with the shop" button. Renders nothing without a WhatsApp number. */
export default function WhatsAppButton() {
  const { t } = useTranslation();
  const { identity } = useStore();
  const { pathname } = useLocation();
  const visible = useRevealOnMobile();
  const href = whatsappUrl(identity.whatsapp);
  if (!href || HIDDEN_ON.some((re) => re.test(pathname))) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('whatsapp.label', { name: identity.name })}
      aria-hidden={visible ? undefined : true}
      tabIndex={visible ? undefined : -1}
      className={`fixed bottom-4 right-4 z-30 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#1F7A4D] px-3.5 text-white shadow-[0_8px_24px_-8px_rgb(0_0_0/0.35)] transition hover:bg-[#19663F] md:bottom-6 md:right-6 md:px-5 ${
        visible ? 'opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
      }`}
    >
      <WhatsAppIcon className="h-6 w-6" />
      <span className="hidden text-sm font-semibold md:inline">{t('whatsapp.cta')}</span>
    </a>
  );
}
